<template>
  <div class="RendererPowerpoint">
    <UISpinner v-if="loading" size="md" class="RendererPowerpoint__loader" />
    <div v-else-if="displayError" class="RendererPowerpoint__error">
      {{ displayError }}
    </div>
    <template v-else>
      <div ref="stageRef" class="RendererPowerpoint__stage">
        <div ref="viewerMountRef" class="RendererPowerpoint__viewport" />
      </div>
      <div class="RendererPowerpoint__controls">
        <UIButton
          class="RendererPowerpoint__control-icon"
          leadIcon="arrowLeftRegular"
          aria-label="Previous slide"
          theme="tertiary"
          size="sm"
          @click="goPrev"
        />
        <div class="RendererPowerpoint__counter">{{ slideLabel }}</div>
        <UIButton
          class="RendererPowerpoint__control-icon"
          leadIcon="arrowRightRegular"
          aria-label="Next slide"
          theme="tertiary"
          size="sm"
          @click="goNext"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  shallowRef,
  watch,
} from 'vue'

import { PptxViewer } from '@aiden0z/pptx-renderer'
import { UIButton, UISpinner } from '@foundever/ui-lib'

interface Props {
  content?: string
  title: string
  buffer: ArrayBuffer | null
  loading?: boolean
  errorMessage?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  content: '',
  loading: false,
  errorMessage: null,
})

const stageRef = ref<HTMLElement | null>(null)
const viewerMountRef = ref<HTMLElement | null>(null)
const viewer = shallowRef<PptxViewer | null>(null)
const localError = ref<string | null>(null)
/** Bumps on slide changes so the counter recomputes (viewer is not reactive). */
const slideTick = ref(0)

const loading = computed(() => props.loading)
const displayError = computed(() => props.errorMessage || localError.value)

const slideLabel = computed(() => {
  void slideTick.value
  const v = viewer.value
  if (!v || v.slideCount < 1) return ''
  return `${v.currentSlideIndex + 1} / ${v.slideCount}`
})

let loadToken = 0
let loadAbort: AbortController | null = null

function onSlideChange(): void {
  slideTick.value += 1
}

function destroyViewerInstance(): void {
  const v = viewer.value
  if (v) {
    v.removeEventListener('slidechange', onSlideChange)
    v.destroy()
    viewer.value = null
  }
  viewerMountRef.value?.replaceChildren()
}

function abortAndClearViewer(): void {
  loadAbort?.abort()
  loadAbort = null
  destroyViewerInstance()
}

defineExpose({
  /** Lets the preview shell nudge layout after split-pane resize; viewer observes its mount box. */
  notifyPreviewPanelResize(): void {
    requestAnimationFrame(() => {
      void viewerMountRef.value?.offsetWidth
    })
  },
})

async function openDeck(buffer: ArrayBuffer): Promise<void> {
  const token = ++loadToken
  loadAbort?.abort()
  loadAbort = new AbortController()
  const { signal } = loadAbort

  const mount = viewerMountRef.value
  if (!mount) return

  destroyViewerInstance()
  localError.value = null

  try {
    const v = await PptxViewer.open(buffer, mount, {
      renderMode: 'slide',
      fitMode: 'contain',
      signal,
    })
    if (token !== loadToken) {
      v.removeEventListener('slidechange', onSlideChange)
      v.destroy()
      return
    }
    v.addEventListener('slidechange', onSlideChange)
    viewer.value = v
    slideTick.value += 1
  } catch (e) {
    if (token !== loadToken) return
    if (e instanceof DOMException && e.name === 'AbortError') return
    localError.value =
      e instanceof Error ? e.message : 'Could not render PowerPoint preview'
  }
}

watch(
  () => [props.buffer, props.loading, props.errorMessage] as const,
  async ([buffer, isLoading, err]) => {
    if (!buffer || isLoading || err) {
      loadToken += 1
      abortAndClearViewer()
      localError.value = null
      return
    }
    await nextTick()
    await openDeck(buffer)
  },
  { flush: 'post', immediate: true },
)

async function goPrev(): Promise<void> {
  const v = viewer.value
  if (!v || v.slideCount < 1 || v.isRendering) return
  try {
    const cur = v.currentSlideIndex
    const n = v.slideCount
    const next = cur <= 0 ? n - 1 : cur - 1
    await v.goToSlide(next)
  } catch {
    /* ignore */
  }
}

async function goNext(): Promise<void> {
  const v = viewer.value
  if (!v || v.slideCount < 1 || v.isRendering) return
  try {
    const cur = v.currentSlideIndex
    const n = v.slideCount
    const next = cur >= n - 1 ? 0 : cur + 1
    await v.goToSlide(next)
  } catch {
    /* ignore */
  }
}

onBeforeUnmount(() => {
  loadToken += 1
  abortAndClearViewer()
})
</script>

<style scoped lang="scss">
.RendererPowerpoint {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  .RendererPowerpoint__controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
    justify-content: center;
    padding: 1rem 0;
  }

  .RendererPowerpoint__counter {
    @include font-size-12px;
    color: var(--text-tertiary);
    min-width: 4.5rem;
    text-align: center;
  }

  .RendererPowerpoint__loader {
    margin: auto;
  }

  .RendererPowerpoint__error {
    @include font-size-14px;
    color: var(--text-status-error);
    padding: 1rem 0;
  }

  .RendererPowerpoint__stage {
    flex: 1;
    min-height: 0;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    @include customScrollbar;
    background: var(--bg-primary);
  }

  .RendererPowerpoint__viewport {
    flex: 1;
    min-height: 0;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: center;
  }
}
</style>
